import { useState } from 'react';
import { useT } from '../../contexts/LocaleContext';

/* ── Sparkline: smooth area + line ── */
export function Sparkline({ data, color = '#1A4A8A', w = 96, h = 30 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - 3 - ((v - min) / range) * (h - 6)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const gid = `sg${color.replace('#', '')}`;
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.4" fill={color} />
    </svg>
  );
}

/* ── Trend chip ── */
export function TrendChip({ dir, value, muted }) {
  const up = dir === 'up';
  const c = muted ? '#6B7280' : up ? '#1A7A4A' : '#C0392B';
  const bg = muted ? '#F3F4F6' : up ? '#E7F5EE' : '#FBEAE8';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, fontWeight: 700,
      color: c, background: bg, padding: '2px 7px 2px 5px', borderRadius: 20, lineHeight: 1.2, flexShrink: 0 }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="3"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: up ? 'none' : 'rotate(180deg)' }}>
        <path d="M12 19V5M5 12l7-7 7 7" />
      </svg>
      {value}
    </span>
  );
}

/* ── Revenue chart: grouped bars ── */
function Legend({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
      <span style={{ width: 9, height: 9, borderRadius: 2.5, background: color }} />
      {label}
    </span>
  );
}

export function RevenueChart({ data }) {
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) {
    return <div className="h-[180px] flex items-center justify-center text-[13px] text-[#9CA3AF]">No invoice data yet.</div>;
  }
  const max = Math.max(...data.map((d) => Math.max(d.invoiced, d.collected)), 1);
  const H = 188, padB = 22;
  const niceMax = Math.max(Math.ceil(max / 20) * 20, 20);
  const gridVals = [0, niceMax / 2, niceMax];
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 14 }}>
        <Legend color="#1A4A8A" label="Invoiced" />
        <Legend color="#1A7A4A" label="Collected" />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {/* Y axis */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          height: H - padB, fontSize: 10.5, color: '#9CA3AF', fontVariantNumeric: 'tabular-nums',
          textAlign: 'right', width: 32, flexShrink: 0 }}>
          {[...gridVals].reverse().map((v) => <span key={v}>£{v}k</span>)}
        </div>
        {/* Plot area */}
        <div style={{ position: 'relative', flex: 1, height: H }}>
          {gridVals.map((v) => (
            <div key={v} style={{ position: 'absolute', left: 0, right: 0,
              bottom: padB + (v / niceMax) * (H - padB), borderTop: '1px dashed #EDEFF2' }} />
          ))}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
            justifyContent: 'space-between', gap: '2.4%' }}>
            {data.map((d, i) => {
              const ih = Math.max(((d.invoiced || 0) / niceMax) * (H - padB), 0);
              const ch = Math.max(((d.collected || 0) / niceMax) * (H - padB), 0);
              const isH = hover === i;
              return (
                <div key={d.m}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'flex-end', cursor: 'default', position: 'relative' }}>
                  {isH && (
                    <div style={{ position: 'absolute', bottom: padB + ih + 8, background: '#0D2240', color: '#fff',
                      padding: '7px 9px', borderRadius: 8, fontSize: 11, whiteSpace: 'nowrap', zIndex: 5,
                      boxShadow: '0 6px 18px rgba(13,34,64,.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 3 }}>{d.m} {new Date().getFullYear()}</div>
                      <div style={{ display: 'flex', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: '#9DC0F0' }}>Inv £{d.invoiced}k</span>
                        <span style={{ color: '#7FD3A8' }}>Col £{d.collected}k</span>
                      </div>
                    </div>
                  )}
                  <div style={{ position: 'absolute', bottom: padB, display: 'flex', gap: 3, alignItems: 'flex-end', height: H - padB }}>
                    <div style={{ width: 11, height: ih, background: '#1A4A8A', borderRadius: '3px 3px 0 0',
                      opacity: d.future ? 0.42 : (hover == null || isH ? 1 : 0.5), transition: 'opacity .15s' }} />
                    <div style={{ width: 11, height: ch, background: '#1A7A4A', borderRadius: '3px 3px 0 0',
                      opacity: d.future ? 0.42 : (hover == null || isH ? 1 : 0.5), transition: 'opacity .15s' }} />
                  </div>
                  <span style={{ position: 'absolute', bottom: 0, fontSize: 10.5,
                    color: isH ? '#0D2240' : '#9CA3AF', fontWeight: isH ? 700 : 500 }}>
                    {d.m}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Pipeline funnel: horizontal stage bars ── */
export function PipelineFunnel({ data, colors, labels }) {
  const t = useT();
  const max = Math.max(...data.map((d) => d.count), 1);
  const total = data.reduce((s, d) => s + d.count, 0);
  function fmtShort(n) {
    if (n >= 1e6) return `£${(n / 1e6).toFixed(1)}m`;
    if (n >= 1e3) return `£${(n / 1e3).toFixed(0)}k`;
    return `£${n.toFixed(0)}`;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      {data.map((d) => {
        const color = colors[d.key] || '#1A4A8A';
        const label = labels[d.key] || d.key;
        const pct = (d.count / max) * 100;
        return (
          <div key={d.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{label}</span>
              <span style={{ fontSize: 11.5, color: '#9CA3AF', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}>
                <b style={{ color: '#111827', fontWeight: 800 }}>{d.count}</b>
                {d.value > 0 && ` · ${fmtShort(d.value)}`}
              </span>
            </div>
            <div style={{ height: 9, background: '#F1F3F6', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6, transition: 'width .5s ease' }} />
            </div>
          </div>
        );
      })}
      <div style={{ marginTop: 2, paddingTop: 12, borderTop: '1px solid #EDEFF2',
        display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <span style={{ color: '#6B7280', fontWeight: 600 }}>{t('dashboard.totalLivePipeline')}</span>
        <span style={{ color: '#0D2240', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{total} tours</span>
      </div>
    </div>
  );
}

/* ── Stacked workload bar ── */
export function WorkloadBar({ data, colors, shortLabels }) {
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  return (
    <div>
      <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#F1F3F6' }}>
        {data.map((d) => (
          <div key={d.key}
            title={`${shortLabels[d.key] || d.key}: ${d.count}`}
            style={{ width: `${(d.count / total) * 100}%`, background: colors[d.key] || '#9CA3AF' }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 12 }}>
        {data.map((d) => (
          <span key={d.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11.5, color: '#6B7280', fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: colors[d.key] || '#9CA3AF' }} />
            {shortLabels[d.key] || d.key} <b style={{ color: '#111827', fontWeight: 800 }}>{d.count}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Progress ring ── */
export function ProgressRing({ done, total, color, size = 30 }) {
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? done / total : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDEFF2" strokeWidth="3" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset .5s' }} />
      <text x="50%" y="51%" textAnchor="middle" dominantBaseline="central"
        fontSize="9" fontWeight="800" fill="#0D2240"
        style={{ fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(pct * 100)}
      </text>
    </svg>
  );
}
